import { Card } from 'components/Card';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { Page } from 'components/Page';
import { useEsc } from 'hooks/useEsc';
import React from 'react';
import { useDispatch } from 'react-redux';
import { eventColorSelected, NAVIGATE_MODAL_BACK } from 'store/actions';
import { PageHeader } from 'components/PageHeader';
import { CardRow } from 'components/CardRow';
import { FontFamilyRegular, theme } from 'lib/theme';
import ColorPicker, {
  HueSlider,
  InputWidget,
  Panel1,
  Swatches,
  ColorFormatsObject,
} from 'reanimated-color-picker';
import { TEXT_SIZE_NORMAL } from 'components/Txt';
import { useConfirmUnsaved } from 'hooks/useConfirm';
import { Platform, View } from 'react-native';

export const ColorPickerModal: React.FC<
  ModalScreenProps<ModalScreens.ColorPicker>
> = ({ route }) => {
  const dispatch = useDispatch();
  const confirmUnsaved = useConfirmUnsaved();

  const { color, pageKey } = route.params;

  const initDataRef = React.useRef<string>(color ?? '#F5CD10');
  const dataRef = React.useRef(initDataRef.current);

  const handleSave = React.useCallback(() => {
    dispatch(
      eventColorSelected({
        key: pageKey,
        color: dataRef.current,
      }),
    );

    dispatch(NAVIGATE_MODAL_BACK());
  }, [dispatch, pageKey]);

  const handleClose = React.useCallback(() => {
    if (dataRef.current !== color) {
      confirmUnsaved({
        onSave: handleSave,
        onDiscard: () => dispatch(NAVIGATE_MODAL_BACK()),
      });
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK());
  }, [dispatch, confirmUnsaved, handleSave, color]);

  useEsc(handleClose);

  const handleColourPick = React.useCallback((col: ColorFormatsObject) => {
    dataRef.current = col.hex;

    void col;
  }, []);

  return (
    <Page
      header={
        <PageHeader
          title="Color Picker"
          onPressCancelButton={handleClose}
          onPressSaveButton={handleSave}
        />
      }
      withSafeAreaTop
      withSafeAreaBottom
    >
      <ColorPicker
        value={color}
        sliderThickness={25}
        thumbSize={24}
        thumbShape="circle"
        onCompleteJS={handleColourPick}
        boundedThumb
        style={styles.colorPicker}
      >
        <Panel1 style={styles.panel} />
        <View
          style={{
            gap: theme.padding,
            paddingBottom: theme.paddingPageVertical,
          }}
        >
          <Card>
            <CardRow style={styles.sliderRow}>
              <HueSlider />
            </CardRow>
          </Card>
          <Card style={styles.inputCard}>
            <CardRow
              style={{
                ...styles.inputRow,
                paddingBottom:
                  Platform.OS !== 'web'
                    ? theme.paddingPageVertical * 1.5
                    : undefined,
              }}
              compact
              small
            >
              <InputWidget
                formats={['HEX']}
                inputTitleStyle={styles.inputTitle}
                containerStyle={styles.inputContainer}
                inputStyle={styles.input}
              />
            </CardRow>
          </Card>
          <Card>
            <CardRow style={styles.swatchesRow} compact small>
              <Swatches
                colors={['#F5CD10', '#ECA317', '#E4781D', '#DB4E24', '#D2232A']}
                style={styles.swatches}
                swatchStyle={styles.swatch}
              />
            </CardRow>
          </Card>
        </View>
      </ColorPicker>
    </Page>
  );
};

type ColorPickerModalStyles = {
  colorPicker: React.ComponentProps<typeof ColorPicker>['style'];
  panel: React.ComponentProps<typeof Panel1>['style'];
  swatchesRow: React.ComponentProps<typeof CardRow>['style'];
  swatches: React.ComponentProps<typeof Swatches>['style'];
  swatch: React.ComponentProps<typeof Swatches>['swatchStyle'];
  sliderRow: React.ComponentProps<typeof CardRow>['style'];
  inputCard: React.ComponentProps<typeof Card>['style'];
  inputRow: React.ComponentProps<typeof CardRow>['style'];
  inputTitle: React.ComponentProps<typeof InputWidget>['inputTitleStyle'];
  inputContainer: React.ComponentProps<typeof InputWidget>['containerStyle'];
  input: React.ComponentProps<typeof InputWidget>['inputStyle'];
};

const styles: ColorPickerModalStyles = {
  colorPicker: {
    gap: theme.padding,
    marginBottom: theme.paddingPageVertical,
    height: '100%',
  },
  panel: {
    flexGrow: 1,
    marginHorizontal: theme.paddingHorizontalHalf * 2,
  },
  swatchesRow: {
    borderRadius: theme.cardBorderRadius,
    marginHorizontal: 0,
  },
  swatches: {
    padding: 0,
  },
  swatch: {
    shadowColor: theme.colors.background,
    borderRadius: '2px',
    margin: 0,
  },
  sliderRow: {
    borderRadius: theme.cardBorderRadius,
  },
  inputCard: {
    padding: 0,
  },
  inputRow: {
    borderRadius: theme.cardBorderRadius,
    padding: 0,
  },
  inputTitle: {
    display: 'none',
    paddingVertical: 0,
  },
  inputContainer: {
    alignItems: 'center',
    flexDirection: 'row',
  },
  input: {
    backgroundColor: 'transparent',
    color: theme.colors.text,
    flex: 1,
    width: '100%',
    fontFamily: FontFamilyRegular,
    fontSize: TEXT_SIZE_NORMAL,
    minHeight: 40,
    maxHeight: 40,
    paddingHorizontal: theme.padding,
    paddingVertical: 0,
    borderColor: 'transparent',
  },
};
